import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ChevronDown, ChevronUp, Download, LoaderCircle } from "lucide-react";
import { Button } from "@/components/ui";
import { ChangeSection } from "./ChangeSection";
import { formatDate, getBadgeClass, organizeChanges } from "./utils";
function getReleaseTitle(changelog) {
    if (changelog.distribution === 'blast-c') {
        return `BLAST C SDK ${changelog.version}`;
    }
    if (changelog.distribution === 'blast-cpp') {
        return `BLAST C++ SDK ${changelog.version}`;
    }
    return changelog.title;
}
function getFilePlatform(fileName) {
    if (!fileName) return 'File';
    if (fileName.includes('linux-aarch64')) return 'Linux aarch64';
    if (fileName.includes('linux-x86_64')) return 'Linux x86_64';
    return 'File';
}
export function ChangelogCard({ changelog, files, isExpanded, onToggleExpand, onDownload, isDownloading, isLatest = false }) {
    const changes = changelog.changes || [];
    const organized = organizeChanges(changes);
    const hasChanges = changes.length > 0;
    const releaseFiles = files ?? [];
    const showFileList = releaseFiles.length > 0;
    const badges = changelog.badges ?? [];
    const showBadges = (hasChanges || showFileList) && badges.length > 0;
    // Determine which sections to show
    const visibleSections = [];
    if (organized.breaking.length > 0) visibleSections.push('breaking');
    if (organized.added.length > 0) visibleSections.push('added');
    if (organized.changed.length > 0) visibleSections.push('changed');
    if (organized.fixed.length > 0) visibleSections.push('fixed');
    if (organized.security.length > 0) visibleSections.push('security');
    // Count items that would be in expandable section
    const expandableCount = Math.max(0, organized.fixed.length - 3) + Math.max(0, organized.security.length - 3);
    return /*#__PURE__*/ _jsxs("article", {
        className: "bg-white border border-gray-200 rounded-lg overflow-hidden",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: `px-6 py-4 flex items-center justify-between ${hasChanges || showFileList ? 'border-b border-gray-200' : ''} ${hasChanges || showFileList ? 'bg-gray-50' : 'bg-white'}`,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-4",
                        children: [
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-xl font-bold text-gray-900 whitespace-nowrap",
                                children: [
                                    "v",
                                    changelog.version
                                ]
                            }),
                            !hasChanges && !showFileList && /*#__PURE__*/ _jsx("span", {
                                className: "text-sm font-semibold text-gray-900",
                                children: changelog.title
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-sm text-gray-500",
                                children: formatDate(changelog.releaseDate)
                            }),
                            showBadges && /*#__PURE__*/ _jsx("div", {
                                className: "flex gap-2",
                                children: badges.filter((badge)=>badge !== 'latest' || isLatest).map((badge)=>/*#__PURE__*/ _jsx("span", {
                                        className: `px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${getBadgeClass(badge)}`,
                                        children: badge
                                    }, badge))
                            })
                        ]
                    }),
                    !showFileList && /*#__PURE__*/ _jsxs(Button, {
                        onClick: ()=>onDownload(changelog),
                        disabled: isDownloading,
                        variant: "simple",
                        color: "navy-90",
                        className: "inline-flex items-center gap-2 whitespace-nowrap !px-4 !py-2 text-sm",
                        children: [
                            isDownloading ? /*#__PURE__*/ _jsx(LoaderCircle, {
                                className: "h-4 w-4 animate-spin"
                            }) : /*#__PURE__*/ _jsx(Download, {
                                className: "h-4 w-4"
                            }),
                            isDownloading ? 'Downloading...' : 'Download'
                        ]
                    })
                ]
            }),
            (hasChanges || showFileList) && /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-4 border-b border-gray-100",
                children: /*#__PURE__*/ _jsx("h2", {
                    className: "text-base font-semibold text-gray-900",
                    children: getReleaseTitle(changelog)
                })
            }),
            showFileList && /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-4 border-b border-gray-100",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "divide-y divide-gray-100",
                    children: releaseFiles.map((file)=>/*#__PURE__*/ _jsxs("div", {
                            className: "flex items-center justify-between gap-4 py-3 bg-white",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "min-w-0 flex items-center gap-3",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "shrink-0 rounded-full bg-navy-5 px-2.5 py-1 text-xs font-medium text-gray-700",
                                            children: getFilePlatform(file.fileName)
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "truncate text-sm text-gray-800",
                                            children: file.fileName ?? file.title
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Button, {
                                    onClick: ()=>onDownload(file),
                                    disabled: isDownloading,
                                    variant: "simple",
                                    color: "navy-90",
                                    className: "shrink-0 inline-flex items-center gap-2 whitespace-nowrap !px-3 !py-1.5 text-sm",
                                    children: [
                                        isDownloading ? /*#__PURE__*/ _jsx(LoaderCircle, {
                                            className: "h-4 w-4 animate-spin"
                                        }) : /*#__PURE__*/ _jsx(Download, {
                                            className: "h-4 w-4"
                                        }),
                                        isDownloading ? 'Downloading...' : 'Download'
                                    ]
                                })
                            ]
                        }, file.changelogId))
                })
            }),
            hasChanges && /*#__PURE__*/ _jsxs("div", {
                className: "px-6 py-6",
                children: [
                    visibleSections.map((sectionType)=>{
                        const sectionChanges = organized[sectionType];
                        // For fixed and security, show first 3 items if not expanded
                        const maxVisible = (sectionType === 'fixed' || sectionType === 'security') && !isExpanded ? 3 : undefined;
                        return /*#__PURE__*/ _jsx(ChangeSection, {
                            type: sectionType,
                            changes: sectionChanges,
                            maxVisible: maxVisible
                        }, sectionType);
                    }),
                    expandableCount > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                onClick: onToggleExpand,
                                className: "flex items-center gap-2 px-4 py-2.5 mt-4 bg-navy-5 border-none rounded-md text-gray-600 text-sm font-medium hover:bg-gray-200 hover:text-gray-900 transition-colors",
                                children: isExpanded ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(ChevronUp, {
                                            className: "h-4 w-4"
                                        }),
                                        "Show less"
                                    ]
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(ChevronDown, {
                                            className: "h-4 w-4"
                                        }),
                                        "Show ",
                                        expandableCount,
                                        " more changes"
                                    ]
                                })
                            }),
                            isExpanded && /*#__PURE__*/ _jsx("div", {
                                className: "mt-4 pt-4 border-t border-gray-200",
                                children: [
                                    'fixed',
                                    'security'
                                ].map((sectionType)=>{
                                    const sectionChanges = organized[sectionType];
                                    if (sectionChanges.length <= 3) return null;
                                    const hiddenChanges = sectionChanges.slice(3);
                                    if (hiddenChanges.length === 0) return null;
                                    return /*#__PURE__*/ _jsx(ChangeSection, {
                                        type: sectionType,
                                        changes: hiddenChanges
                                    }, `${sectionType}-hidden`);
                                })
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
